import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import {
  exportProgramEarningsMonthlyCSV,
  getProgramEarningsMonthlySummary,
  getProgramEarningsYearlySummary,
} from '@/services/eos/program-earnings-dashboard.service'
import { useMutation, useQuery } from '@tanstack/vue-query'
import type { Ref } from 'vue'
import { isUndefined } from 'lodash'

export enum MISOProgram {
  All = 'all',
  /** Day-Ahead Price-Response */
  DAPR = 'MISO Price Response',
  /** Operating reserves */
  OR = 'MISO Operating Reserves',
}

function isDefined<T>(value: T | undefined): value is T {
  return !isUndefined(value)
}

export default function useProgramEarningsService() {
  /** Year summary, charts data */
  const yearEarningsSummaryQuery = (
    site: Ref<ISiteIdentifier | undefined>,
    year: Ref<number | undefined>,
    enabled?: Ref<boolean>
  ) =>
    useQuery({
      queryFn: () => getProgramEarningsYearlySummary(site.value!.site_id!, year.value!),
      queryKey: ['program-earnings-yearly-summary', site, year],
      enabled: () => isDefined(site.value?.site_id) && isDefined(year.value) && (enabled ? enabled.value : true),
    })

  /** Month summary,  */
  const monthEarningsSummaryQuery = (
    site: Ref<ISiteIdentifier | undefined>,
    program: Ref<MISOProgram | undefined>,
    year: Ref<number | undefined>,
    month: Ref<number | undefined>
  ) =>
    useQuery({
      queryFn: () =>
        getProgramEarningsMonthlySummary(site.value!.site_id!, program.value!, year.value!, month.value! + 1),
      queryKey: ['program-earnings-monthly-summary', site, year, month, program],
      enabled: () =>
        isDefined(site.value?.site_id) && isDefined(year.value) && isDefined(month.value) && isDefined(program.value),
    })

  const exportMonthlyEarningsMutation = useMutation({
    mutationFn: ({ siteId, program, year, month }: { siteId: string; program: string; year: number; month: number }) =>
      exportProgramEarningsMonthlyCSV(siteId, program, year, month + 1),
  })

  return { yearEarningsSummaryQuery, monthEarningsSummaryQuery, exportMonthlyEarningsMutation }
}
